import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Table } from '@/components';
import { loadingTableHandler } from '@/utils/handler/loadingTableHandler';
import { formatDealerAmount } from '@/utils/currency';
import { Button, Space, Tag } from 'antd';
import { FaCheck, FaTimes } from 'react-icons/fa';
import type { ColumnsType } from 'antd/es/table';
import { IDataverseOrder } from '@/features/orders-management/types';
import { getOrderNumber, getOrderStatusColor } from '@/features/orders-management/utils';
import {
  ORDER_STATUS_LABELS,
  PAYMENT_STATUS_COLORS,
  PAYMENT_STATUS_LABELS,
} from '@/features/orders-management/constants';
import { ROUTESPATH } from '@/routes';
import { format } from 'date-fns';

// Keep the legacy Order interface for backward compatibility
export interface Order {
  id: string;
  orderNumber: string;
  customerName: string;
  customerCompany: string;
  orderValue: number;
  currentCreditLimit: number;
  currentOutstanding: number;
  requestedCredit: number;
  orderDate: Date;
  salesRep: string;
  region: string;
  status: 'pending_credit' | 'credit_approved' | 'credit_rejected';
  notes?: string;
}

interface CreditOrdersTableProps {
  orders: IDataverseOrder[];
  onApprove: (order: IDataverseOrder) => void;
  onReject: (order: IDataverseOrder) => void;
  canApproveCredit?: boolean;
  loading?: boolean;
  current?: number;
  pageSize?: number;
  total?: number;
  nextLink?: string;
  totalRecordCountLimitExceeded?: boolean;
  onPageChange?: (page: number, pageSize: number) => void;
}

export const CreditOrdersTable: React.FC<CreditOrdersTableProps> = ({
  orders,
  onApprove,
  onReject,
  canApproveCredit = true,
  loading = false,
  current,
  pageSize,
  total,
  nextLink,
  totalRecordCountLimitExceeded,
  onPageChange,
}) => {
  const navigate = useNavigate();
  const columns: ColumnsType<IDataverseOrder> = [
    {
      title: 'Order',
      key: 'ordernumber',
      width: 150,
      render: (_: unknown, record: IDataverseOrder) => (
        <span
          className="font-semibold text-primary-500 hover:text-primary-500 cursor-pointer hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            navigate(ROUTESPATH.ORDER_DETAIL.replace(':id', record.salesorderid));
          }}
        >
          {getOrderNumber(record)}
        </span>
      ),
    },
    {
      title: 'Customer',
      key: 'customer',
      width: 200,
      render: (_: any, record: IDataverseOrder) => (
        <div>
          <div className="font-medium text-gray-900">
            {record['_customerid_value@OData.Community.Display.V1.FormattedValue'] || 'N/A'}
          </div>
          <div className="text-sm text-gray-500">{record.name}</div>
        </div>
      ),
    },
    {
      title: 'Company',
      key: 'company',
      width: 200,
      render: (_: any, record: IDataverseOrder) => (
        record.quoteid?.['_dogma_customer_value@OData.Community.Display.V1.FormattedValue'] || 'N/A'
      ),
    },
    {
      title: 'Order Value',
      dataIndex: 'totalamount_base',
      key: 'totalamount_base',
      width: 130,
      render: (_: number, record: IDataverseOrder) => {
        const value = record.dogma_vsstotalamount || 0;
        const dealersCurrency = record.quoteid?.dogma_DealersCurrency;
        const exchangeRate = record.quoteid?.dogma_exchangerate ?? 1;
        return (
          <span className="font-semibold text-gray-900">
            {formatDealerAmount(value, dealersCurrency, exchangeRate)}
          </span>
        );
      },
    },
    {
      title: 'Order Status',
      key: 'statuscode',
      width: 150,
      render: (_: unknown, record: IDataverseOrder) => {
        const label = record['statuscode@OData.Community.Display.V1.FormattedValue'] || ORDER_STATUS_LABELS[record.statuscode] || 'Unknown';
        return (
          <Tag
            color={getOrderStatusColor(record.statuscode, label)}
            style={{ borderRadius: '12px', padding: '4px 12px', border: 'none' }}
          >
            {label}
          </Tag>
        );
      },
    },
    {
      title: 'Payment Status',
      key: 'dogma_paymentstatus',
      width: 150,
      render: (_: unknown, record: IDataverseOrder) => (
        <Tag
          color={PAYMENT_STATUS_COLORS[record.dogma_paymentstatus] || 'default'}
          style={{ borderRadius: '12px', padding: '4px 12px', border: 'none' }}
        >
          {record['dogma_paymentstatus@OData.Community.Display.V1.FormattedValue'] || PAYMENT_STATUS_LABELS[record.dogma_paymentstatus] || 'Unknown'}
        </Tag>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'createdon',
      key: 'createdon',
      width: 120,
      render: (date: string) => date ? format(new Date(date), 'dd/MM/yyyy') : 'N/A',
    },
    ...(canApproveCredit ? [{
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: IDataverseOrder) => (
        <Space size="small" wrap>
          <Button
            type="primary"
            size="small"
            icon={<FaCheck />}
            onClick={() => onApprove(record)}
            className="bg-green-600 hover:bg-green-700"
          >
            Approve
          </Button>
          <Button
            danger
            size="small"
            icon={<FaTimes />}
            onClick={() => onReject(record)}
          >
            Reject
          </Button>
        </Space>
      ),
    }] : []),
  ];

  return (
    <div className="bg-white rounded-lg">
      <div className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Orders Pending Credit Approval
        </h2>
        {loadingTableHandler(
          loading,
          <Table<IDataverseOrder>
            columns={columns}
            dataSource={orders}
            rowKey="salesorderid"
            isLoading={loading}
            current={current}
            pageSize={pageSize}
            total={total}
            nextLink={nextLink}
            totalRecordCountLimitExceeded={totalRecordCountLimitExceeded}
            handleChange={onPageChange}
            scroll={{ x: 1200 }}
          />
        )}
      </div>
    </div>
  );
};

export default CreditOrdersTable;
